import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { pricingCategory, priceTypes } from "../../../enums/pricing.enum";
import { currencyDisplay } from "../../../utils/helpers";

interface PriceChartV3Props {
  priceCurrency: string;
  prices: any[];
  roomTypes?: Record<string, string>;
  isB2C2B?: boolean;
  isMobile?: boolean;
}

const PROMO_TYPES = new Set(["specialoffer", "specialdeal", "superpromo", "early_bird"]);
const priceTypeLabels = {
  specialoffer: 'Special Offer',
  specialdeal: 'Special Deals',
  superpromo: 'Super Promo',
  early_bird: 'Early Bird',
};
const isPromoType = (type: string) =>
  type?.startsWith("promo") || PROMO_TYPES.has(type);

// Split "Adult (Single Room)" → ["Adult", "(Single Room)"]
// const parseCatLabel = (label: string): [string, string | null] => {
//   const match = label.match(/^(.+?)\s*(\(.+\))$/);
//   return match ? [match[1].trim(), match[2]] : [label, null];
// };
const parseCatLabel = (label: string, isMobile: boolean): [string, string | null] => {
  // Normalize known labels to the desired "Base (description)" format
  const normalized = isMobile ? label : label
    .replace(/^Child with Extra bed$/i, "Child (with Extra Bed)")
    .replace(/^Child without bed$/i, "Child (without bed)");

  const match = normalized.match(/^(.+?)\s*(\(.+\))$/);

  return match
    ? [match[1].trim(), match[2]]
    : [normalized, null];
};


const PriceChartV3: React.FC<PriceChartV3Props> = ({ priceCurrency, prices, roomTypes, isMobile }) => {
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const categories = pricingCategory(roomTypes || type || undefined);

  const priceLabel = (val: any) =>
    val == null || val === "" || Number(val) === 0
      ? "TBC"
      : currencyDisplay(priceCurrency, val);

  const tierLabel = (price: any) =>
    price.display_price || priceTypes[price.type] || price.type;

  // With a single tier the fixed-width columns leave the bordered box half-empty;
  // a modifier lets the lone tier column stretch to fill the drawer width.
  const isSingleTier = prices.length === 1;
  
  const chartRef = useRef<HTMLDivElement>(null);
  const [hasScrollbar, setHasScrollbar] = useState(false);
  useEffect(() => {
    const el = chartRef.current;
    if (!el) return;
    const checkScrollbar = () => {
      setHasScrollbar(el.scrollWidth > el.clientWidth);
    };
  
    checkScrollbar();
  }, [prices]);

  return (
    <div ref={chartRef} className={`v3-price-chart${isSingleTier ? " v3-price-chart--single" : ""}${hasScrollbar ? ' has-scrollbar' : ''}`}>
      <div className="v3-price-chart_table">
        {/* Sticky category column */}
        <div className="v3-price-chart_col v3-price-chart_col--category">
          <div className="v3-price-chart_header v3-price-chart_header--category">
            Category
          </div>
          {Object.entries(categories).map(([key, label]) => {
            const [main, sub] = parseCatLabel(label, isMobile??false);
            return (
              <div key={key} className="v3-price-chart_cell v3-price-chart_cell--category">
                <span className="v3-price-chart_cat-main">{main}</span>
                {sub && <span className="v3-price-chart_cat-sub">{sub}</span>}
              </div>
            );
          })}
        </div>

        {/* Scrollable price tier columns */}
        <div className="v3-price-chart_promos">
          {prices.map((price) => {
            const promo = isPromoType(price.type);
            return (
              <div
                key={price.type}
                className={`v3-price-chart_col${promo ? " v3-price-chart_col--promo" : " v3-price-chart_col--normal"}`}
              >
                <div
                  className={`v3-price-chart_header v3-price-chart_header--tier${promo ? " v3-price-chart_header--promo" : ""}`}
                >
                  <span className="v3-price-chart_tier-label">{tierLabel(price)}</span>
                  {promo && <span className="v3-price-chart_badge">{(priceTypeLabels[price.type] || price.type).toUpperCase()}</span>}
                </div>
                {Object.keys(categories).map((cat) => (
                  <div
                    key={cat}
                    className={`v3-price-chart_cell${promo ? " v3-price-chart_cell--promo" : ""}`}
                  >
                    {priceLabel(price[cat])}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PriceChartV3;
